import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  NISAddress,
  NISProfessionalDetails,
  NISProfile,
} from "../../api/services/nisParticipants/models";
import { useGetNISParticipantDetailsById } from "../../api/services/nisParticipants/nisParticipantsApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabelWithFormattedPhoneText,
  MuiFooterButtons,
  MuiLink,
  MuiTypography,
  StatusBadge,
  StatusBadgeType,
} from "../../components";
import { ExpandCollapseButton } from "../../components/expandCollapseButton/ExpandCollapseButton";
import {
  ListItem,
  SectionData,
  ViewerWithCollapsibleHeader,
} from "../../components/viewerWithCollapsibleHeader/ViewerWithCollapsibleHeader";
import {
  EMPTY_VALUE_DASH,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../helpers/constants";
import { getDateFormatByCountry } from "../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  createNavigationLink,
  getFullName,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../helpers/utils";
import useAccordion from "../../hooks/useAccordion";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../hooks/usePermissions";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { siteCoreContentLoadingSelector } from "../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { CPParticipantActivityTimelineWrapper } from "../coachPortal/components/cpParticipantActivityTimeline/CPParticipantActivityTimeline.style";
import { NISStatus } from "../createNISParticipant/components/nisStatus/NISStatus";
import {
  NIS_PARTICIPANTS_ACCORDION_KEYS,
  VALUE_OTHER,
} from "../createNISParticipant/modelsAndSchemas/Models";
import {
  createNISParticipantAddressDetailsSiteCoreKeys,
  createNISParticipantNISStatusSiteCoreKeys,
  createNISParticipantProfessionalDetailsSiteCoreKeys,
  createNISParticipantProfileSiteCoreKeys,
  nisParticipantHeaderSiteCoreKeys,
} from "../createNISParticipant/siteCore/SiteCoreKeys";
import ParticipantActivityTimeline from "../createProgram/components/createProgramTabs/tabs/participant/components/participantActivityTimeline/ParticipantActivityTimeline";
import { activityTimelineSiteCoreKeys } from "../createProgram/siteCore/SiteCoreKeys";
import {
  NISParticipantAction,
  nisParticipantActionMap,
} from "../programSearch/components/nisParticipantSearchResults/NISParticipantSearchResult";
import { NISParticipantStatus } from "../programSearch/modelsAndSchemas/models";
import { ViewNISParticipantWrapper } from "./ViewNISParticipant.style";
import { ConvertToProgramNavigation } from "./components/convertToProgramNavigation/ConvertToProgramNavigation";
import ViewNISParticipantHeader from "./components/viewNISParticipantHeader/ViewNISParticipantHeader";
import { isClientEmployerOther, isHRContactOther } from "./helper";

export const viewNISParticipantsInitialAccordionStatus = {
  [NIS_PARTICIPANTS_ACCORDION_KEYS.all]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.profile]: true,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.professionalDetails]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.addressDetails]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.nisStatus]: false,
  [NIS_PARTICIPANTS_ACCORDION_KEYS.activityTimeline]: false,
};

// Exported for testing
export const getMappedAccordionStatus = (
  index: number,
  accordionStatus: typeof viewNISParticipantsInitialAccordionStatus,
): boolean => {
  switch (index) {
    case 0:
      return accordionStatus.profile;
    case 1:
      return accordionStatus.professionalDetails;
    case 2:
      return accordionStatus.addressDetails;
    case 3:
      return accordionStatus.nisStatus;
    case 4:
      return accordionStatus.activityTimeline;
    default:
      return accordionStatus.all;
  }
};

const pageId = PAGE_IDS.VIEW_NIS_PARTICIPANT;
const ViewNISParticipant = () => {
  const {
    accordionStatus,
    handleIndividualAccordionChange,
    handleExpandCollapseAllChange,
  } = useAccordion(viewNISParticipantsInitialAccordionStatus);
  const [allAccordionsData, setAllAccordionsData] = useState<SectionData[]>();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const {
    useFetchLabel,
    fetchComponentTitle,
    fetchValueByKeyOfDropdown,
    fetchDropdownListKeyValueForCountrySpecific,
    commonLabels,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const { displayToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id: nisParticipantId } = useParams<{ id: string }>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const [
    showConvertToProgramConfirmationModal,
    setShowConvertToProgramConfirmationModal,
  ] = useState(false);
  const {
    data: nisParticipantData,
    isFetching: isNISParticipantDataLoading,
    error: nisParticipantDataError,
  } = useGetNISParticipantDetailsById(nisParticipantId);

  const participantActivityTimelineMethods = useForm({
    mode: "all",
  });

  useEffect(() => {
    if (nisParticipantDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [nisParticipantDataError]);

  const {
    profileTitle,
    nisID,
    participantFirstName,
    participantLastName,
    jobTitle,
    participantEmail,
    participantWorkEmail,
    participantMobile,
    nisStatus,
    nisParticipantStatusList,
    programKey,
  } = createNISParticipantProfileSiteCoreKeys;

  const {
    addressDetailsTitle,
    addressSubTitle,
    stateRegionProvinceListPrefix,
  } = createNISParticipantAddressDetailsSiteCoreKeys;

  const { activityTimeline } = activityTimelineSiteCoreKeys;

  const {
    professionalDetailsTitle,
    country,
    clientEmployer,
    clientEmployerOther,
    projectManager,
    employeeId,
    releaseDate,
    hrContact,
    hrContactNameOther,
    hrContactEmailIdOther,
    severanceStatusSigned,
    severanceStatusList,
  } = createNISParticipantProfessionalDetailsSiteCoreKeys;

  const { nisStatusTitle } = createNISParticipantNISStatusSiteCoreKeys;

  const getProfileLabel = useFetchLabel(profileTitle);
  const getProfessionalDetailsLabel = useFetchLabel(professionalDetailsTitle);
  const getAddressDetailsLabel = useFetchLabel(addressDetailsTitle);

  const getStateValue = (data: NISAddress) => {
    const stateRegionValue = fetchDropdownListKeyValueForCountrySpecific(
      addressDetailsTitle,
      stateRegionProvinceListPrefix,
      getValueOrBlank(data?.country?.countryId),
      getValueOrBlank(data?.stateId),
    );

    return {
      stateRegionValue: isNotEmpty(stateRegionValue)
        ? stateRegionValue
        : data?.stateId,
    };
  };

  const getFormattedAddress = (data: NISAddress) => {
    // Address: Address1, Address2, Address3, City, state, Country, Pin code.
    const stateValue = getStateValue(data).stateRegionValue;
    const addressParts = [
      data?.address1,
      data?.address2,
      data?.address3,
      data?.city,
      stateValue,
      data?.country?.countryName,
    ].filter((part) => part?.trim());
    if (isEmpty(addressParts.length)) return "";
    const formattedAddress = addressParts.filter((part) => part).join(", ");
    if (isNotEmpty(data?.postalCode)) {
      return `${formattedAddress} - ${data?.postalCode}`;
    }
    return formattedAddress;
  };

  const getProfileData = (data: NISProfile): ListItem[] => {
    return [
      {
        label: nisID,
        value: data.nisId,
        isValueWithEllipsis: false,
      },
      {
        label: participantFirstName,
        value: data.firstName,
        isValueWithEllipsis: true,
      },
      {
        label: participantLastName,
        value: data.lastName,
        isValueWithEllipsis: true,
      },
      {
        label: jobTitle,
        value: data.jobTitle,
      },
      {
        label: participantEmail,
        value: data.email,
        isValueWithEllipsis: true,
      },
      {
        label: participantWorkEmail,
        value: data.workEmail,
        isValueWithEllipsis: true,
      },
      {
        label: participantMobile,
        value: (
          <MuiDoubleLabelWithFormattedPhoneText
            id={participantMobile}
            label={""}
            replaceEmptyWithDash
            selectedCountry={getValueOrBlank(data.mobileNumber?.isoCode)}
            phoneNumber={getValueOrBlank(data.mobileNumber?.number)}
            countryCode={getValueOrBlank(data.mobileNumber?.countryCode)}
          />
        ),
      },
      {
        label: nisStatus,
        value: (
          <StatusBadge
            status={data.statusId as StatusBadgeType}
            label={fetchValueByKeyOfDropdown(
              profileTitle,
              nisParticipantStatusList,
              data.statusId,
            )}
          />
        ),
      },
      ...(isStatusConvertedToProgram(data)
        ? [
            {
              label: programKey,
              value: isNotEmpty(data?.program?.id) ? (
                <MuiLink
                  href={createNavigationLink(
                    PAGE_URLS.VIEW_PROGRAM,
                    getValueOrBlank(data?.program?.id),
                  )}
                  isOpenNewTab
                >
                  <MuiBox>
                    <MuiTypography variant="h4Bold" className="Link">
                      {data?.program?.keyNumber ?? EMPTY_VALUE_DASH}
                    </MuiTypography>
                  </MuiBox>
                </MuiLink>
              ) : (
                EMPTY_VALUE_DASH
              ),
            },
          ]
        : []),
    ];
  };

  const getProjectManagerDetails = (data: NISProfessionalDetails): string => {
    if (!data?.projectManager) {
      return getValueOrBlank(data?.serviceId);
    }
    return getFullName(
      data.projectManager.firstName,
      data.projectManager.lastName,
    );
  };

  const getProfessionalDetailsData = (
    data: NISProfessionalDetails,
  ): ListItem[] => {
    return [
      {
        label: country,
        value: data.country.countryName,
      },
      {
        label: clientEmployer,
        value: isClientEmployerOther(data) ? VALUE_OTHER : data?.employer?.name,
      },
      ...(isClientEmployerOther(data)
        ? [
            {
              label: clientEmployerOther,
              value: getValueOrBlank(data?.otherEmployer),
            },
          ]
        : []),
      {
        label: projectManager,
        value: getProjectManagerDetails(data),
      },
      {
        label: employeeId,
        value: getValueOrBlank(data.employeeId),
      },
      {
        label: releaseDate,
        value: isNotEmpty(data.releaseDate)
          ? convertToUserFormat(data.releaseDate, userCountryDateFormat)
          : EMPTY_VALUE_DASH,
      },
      {
        label: hrContact,
        value: isHRContactOther(data)
          ? VALUE_OTHER
          : getFullName(data.hrContact?.firstName, data.hrContact?.lastName),
      },
      ...(isHRContactOther(data)
        ? [
            {
              label: hrContactNameOther,
              value: getValueOrBlank(data.otherHRContactName),
            },
            {
              label: hrContactEmailIdOther,
              value: getValueOrBlank(data.otherHRContactEmailId),
            },
          ]
        : []),
      {
        label: severanceStatusSigned,
        value: fetchValueByKeyOfDropdown(
          professionalDetailsTitle,
          severanceStatusList,
          getValueOrBlank(data.severanceStatusSignedId),
        ),
      },
    ];
  };

  const getAddressDetailsData = (data: NISAddress): ListItem[] => {
    return [
      {
        label: addressSubTitle,
        value: getFormattedAddress(data),
      },
    ];
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isViewNISFetching: {
          isVisible: isNISParticipantDataLoading,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(toggleLoader({ isViewNISFetching: { isVisible: false } }));
    };
  }, [isNISParticipantDataLoading]);

  useEffect(() => {
    if (isNotEmpty(nisParticipantData) && !siteCoreLoading) {
      setAllAccordionsData([
        {
          title: fetchComponentTitle(profileTitle),
          data: getProfileData(nisParticipantData.profile),
          labelResolver: getProfileLabel,
          accordionKey: NIS_PARTICIPANTS_ACCORDION_KEYS.profile,
        },
        {
          title: fetchComponentTitle(professionalDetailsTitle),
          data: getProfessionalDetailsData(
            nisParticipantData.professionalDetails,
          ),
          labelResolver: getProfessionalDetailsLabel,
          accordionKey: NIS_PARTICIPANTS_ACCORDION_KEYS.professionalDetails,
        },
        {
          title: fetchComponentTitle(addressDetailsTitle),
          data: getAddressDetailsData(nisParticipantData.address),
          labelResolver: getAddressDetailsLabel,
          accordionKey: NIS_PARTICIPANTS_ACCORDION_KEYS.addressDetails,
        },
      ]);
    }
  }, [nisParticipantData, siteCoreLoading]);

  const goBack = () => {
    navigate(PAGE_URLS.SEARCH_PROGRAM);
  };

  const handleEditClick = () => {
    // Navigate to the edit page with the nisParticipantId and preserve the origin
    navigate(`${PAGE_URLS.EDIT_NIS_PARTICIPANT}/${nisParticipantId}`, {
      state: { from: location.state?.from },
    });
  };

  const isStatusConvertedToProgram = (data?: NISProfile): boolean => {
    return data?.statusId === NISParticipantStatus.Converted;
  };

  const shouldHideEditButton = useMemo(
    () =>
      !hasModulePermission(MODULE_PERMISSIONS.NIS_EDIT) ||
      isStatusConvertedToProgram(nisParticipantData?.profile),
    [nisParticipantData, nisParticipantData?.profile],
  );

  const isConvertToProgramButtonVisible = useMemo(() => {
    const statusId = nisParticipantData?.profile?.statusId;
    const allowedActions = nisParticipantActionMap[statusId ?? ""] ?? [];
    return (
      allowedActions.includes(NISParticipantAction.ConvertToProgram) &&
      nisParticipantData &&
      !isClientEmployerOther(nisParticipantData.professionalDetails)
    );
  }, [nisParticipantData, nisParticipantData?.profile?.statusId]);

  const handleConvertToProgramClick = () => {
    setShowConvertToProgramConfirmationModal(true);
  };

  const renderHeader = (label: string) => {
    return <AccordionHeaderText label={label} />;
  };

  return (
    <ViewNISParticipantWrapper>
      <ViewNISParticipantHeader
        onEditClick={handleEditClick}
        onConvertToProgramClick={handleConvertToProgramClick}
        hideEditButton={shouldHideEditButton}
        showConvertToProgramButton={isConvertToProgramButtonVisible}
      />

      <ExpandCollapseButton
        accordionStatus={accordionStatus}
        handleExpandCollapseAllChange={handleExpandCollapseAllChange}
      />

      {allAccordionsData?.map((section, index) => (
        <>
          <ViewerWithCollapsibleHeader
            key={section.accordionKey}
            items={section.data}
            accordionTitle={section.title}
            labelResolver={section.labelResolver}
            isExpanded={getMappedAccordionStatus(index, accordionStatus)}
            onAccordionChange={() =>
              handleIndividualAccordionChange(section.accordionKey)
            }
            closePopoverARIALabel={commonARIALabel.closePopover}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            showMoreARIALabel={commonARIALabel.showNMore}
          />
          <MuiBox className="separator-space" />
        </>
      ))}

      <AccordionHeader
        title={renderHeader(fetchComponentTitle(nisStatusTitle))}
        onChange={() => handleIndividualAccordionChange("nisStatus")}
        isExpanded={accordionStatus.nisStatus}
      >
        <NISStatus
          isEdit={false}
          isViewOnly={true}
          editNISParticipantData={nisParticipantData}
          nisParticipantId={nisParticipantId}
        />
      </AccordionHeader>

      <MuiBox className="separator-space" />

      <AccordionHeader
        title={renderHeader(fetchComponentTitle(activityTimeline))}
        onChange={() => handleIndividualAccordionChange("activityTimeline")}
        isExpanded={accordionStatus.activityTimeline}
      >
        <CPParticipantActivityTimelineWrapper>
          <FormProvider {...participantActivityTimelineMethods}>
            <ParticipantActivityTimeline
              pageId={pageId}
              programId={getValueOrBlank(nisParticipantId)}
              isFromNISParticipant={true}
              displayAddBtn={false}
              showNextContactDate={false}
              isFromCoachPortal={false}
            />
          </FormProvider>
        </CPParticipantActivityTimelineWrapper>
      </AccordionHeader>

      <MuiBox className="separator-space" />

      <MuiFooterButtons
        secondaryButtonLabel={commonLabels.close}
        primaryButtonLabel={commonLabels.edit}
        onSecondaryClick={goBack}
        onPrimaryClick={handleEditClick}
        hidePrimaryButton={shouldHideEditButton}
      />

      {showConvertToProgramConfirmationModal && nisParticipantData && (
        <ConvertToProgramNavigation
          nisParticipantData={nisParticipantData}
          parentKey={nisParticipantHeaderSiteCoreKeys.nisParticipantHeaderTitle}
          confirmationKey={
            nisParticipantHeaderSiteCoreKeys.convertToProgramConfirmationMessage
          }
          showConvertToProgramConfirmationModal={
            showConvertToProgramConfirmationModal
          }
          setShowConvertToProgramConfirmationModal={
            setShowConvertToProgramConfirmationModal
          }
        />
      )}
    </ViewNISParticipantWrapper>
  );
};

export default ViewNISParticipant;
